<template>
  <div
    class="d-flex flex-column justify-content-between charts-table"
    :class="isShowInfoAble ? 'cursor-pointer' : ''"
    @dblclick="isShowInfoAble ? $emit('on-click-data-info') : ''"
  >
    <base-table
      class="base-table"
      :columns="columns"
      :data="data"
      :currentPage="pagination.currentPage"
      :perPages="pagination.perPage"
      @sorting-change="onSort"
      responsive
    >
      <template v-for="column in columns" :key="column.key" v-slot:[renderSlotName(column.key)]="slotData">
        <template v-if="column.columnType != 2">
          <div class="ellipsis-cell" :ref="(el) => registerCellRef(column.key, el)">
            <EllipsisTooltip
              :text="String(slotData.item[column.key])"
              :limit="columnWidths[column.key] ? calculateCharLimit(columnWidths[column.key]) : 30"
            />
          </div>
        </template>
        <template v-else>
          <component :key="column.key" :is="resolveComponent(column.columnType)" :data="slotData.item[column.key]" :rawData="slotData.item" />
        </template>
      </template>
    </base-table>
    <Pagination v-if="isPagination" v-model="pagination.currentPage" :totalRows="pagination.totalRows" :perPages="pagination.perPage" />
  </div>
</template>

<script setup lang="ts">
import BaseTable from '@/views/components/table/base-table/BaseTable.vue';
import Pagination from '@/views/components/pagination/Pagination.vue';

import useChartsTable from './hooks/useChartsTable';

import EllipsisTooltip from '../ellipsis-tooltip/EllipsisTooltip.vue';

import { ref, reactive, nextTick, onMounted } from 'vue';

interface Props {
  columns: ChartTable.TableColumn[];
  data: Record<string, any>[];
  isPagination?: boolean;
  isShowInfoAble?: boolean;
  isSortByLocale?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isPagination: true,
  isShowInfoAble: false,
  isSortByLocale: false,
});
defineEmits(['on-click-data-info']);

const { columns, data, pagination, renderSlotName, resolveComponent, onSort } = useChartsTable(props);

const cellRefs = reactive<Record<string, HTMLElement[]>>({});
const columnWidths = reactive<Record<string, number>>({});

function registerCellRef(key: string, el: unknown) {
  const element = el as HTMLElement;
  if (element && element instanceof HTMLElement) {
    if (!cellRefs[key]) cellRefs[key] = [];
    cellRefs[key].push(element);
  }
}

function calculateCharLimit(width: number): number {
  const avgCharWidth = 7.5; // ปรับตาม font ที่ใช้จริง
  return Math.floor(width / avgCharWidth);
}

onMounted(() => {
  nextTick(() => {
    Object.entries(cellRefs).forEach(([key, els]) => {
      // เอาความกว้างเฉลี่ยของ cell column นั้น
      const widths = els.map((el) => el.offsetWidth);
      const avgWidth = widths.reduce((a, b) => a + b, 0) / widths.length;
      columnWidths[key] = avgWidth;
    });
  });
});
</script>

<style lang="scss" scoped>
@import '@assets/styles/modules/analytics/bi/analytics-bi-charts-table.scss';

.ellipsis-cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}
</style>
